import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Building2, Home as HomeIcon, Trees, Store } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PROPERTY_TYPES = [
  { key: "apartament", label: "Apartament", icon: Building2 },
  { key: "casa", label: "Casă", icon: HomeIcon },
  { key: "teren", label: "Teren", icon: Trees },
  { key: "comercial", label: "Comercial", icon: Store },
];
const CONDITIONS = [
  { key: "lux", label: "Lux", d: "Finisaje premium, mobilier high-end" },
  { key: "renovat", label: "Renovat", d: "Recent modernizat, gata de locuit" },
  { key: "bun", label: "Bună", d: "Stare medie, funcțional" },
  { key: "necesita_renovare", label: "Necesită renovare", d: "Lucrări majore necesare" },
];
const FEATURES = [
  { key: "parcare", label: "Parcare" }, { key: "balcon", label: "Balcon" },
  { key: "lift", label: "Lift" }, { key: "centrala", label: "Centrală" },
  { key: "gradina", label: "Grădină" }, { key: "piscina", label: "Piscină" },
  { key: "terasa", label: "Terasă" }, { key: "vedere_panoramica", label: "Vedere panoramică" },
  { key: "mobilat", label: "Mobilat" },
];

export default function Evaluate() {
  const [step, setStep] = useState(0);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    property_type: "apartament",
    city: "", neighborhood: "",
    surface: "", rooms: "", year_built: "",
    condition: "bun", features: [], description: "",
  });
  const nav = useNavigate();

  useEffect(() => { api.get("/cities").then(r => setCities(r.data)); }, []);

  const steps = ["Tip proprietate", "Locație", "Caracteristici", "Stare & dotări", "Trimite"];

  const next = () => {
    if (step === 0 && !form.property_type) return toast.error("Alege tipul proprietății");
    if (step === 1 && !form.city) return toast.error("Alege orașul");
    if (step === 2 && (!form.surface || Number(form.surface) <= 0)) return toast.error("Introdu o suprafață validă");
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const toggleFeature = (f) => {
    setForm((v) => ({ ...v, features: v.features.includes(f) ? v.features.filter(x => x !== f) : [...v.features, f] }));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        surface: Number(form.surface),
        rooms: form.rooms ? Number(form.rooms) : null,
        year_built: form.year_built ? Number(form.year_built) : null,
      };
      const { data } = await api.post("/evaluate", payload);
      toast.success("Evaluare generată!");
      nav(`/rezultat/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A apărut o eroare");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full border-b border-[#0D1F2D] bg-transparent py-3 px-0 text-lg font-display focus:outline-none focus:border-[#0F6E5B] placeholder:text-[#A0A5AC]";

  return (
    <main data-testid="evaluate-page" className="bg-[#F6F4EE] min-h-screen text-[#0D1F2D]">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-14">
        {/* Progress */}
        <div className="flex items-center justify-between mb-14 overflow-x-auto">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-3 min-w-fit">
              <div className={`w-8 h-8 grid place-items-center border mono text-xs ${i <= step ? "border-[#0F6E5B] bg-[#0F6E5B] text-white" : "border-[#DEDCCF] text-[#52616B]"}`}>{String(i+1).padStart(2,"0")}</div>
              <div className={`text-sm ${i === step ? "font-medium" : "text-[#52616B]"}`}>{s}</div>
              {i < steps.length - 1 && <div className="w-6 h-px bg-[#DEDCCF] mx-2" />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="ee-rise">
          {step === 0 && (
            <div data-testid="step-type">
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Pasul 1</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3">Ce tip de proprietate evaluezi?</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                {PROPERTY_TYPES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`type-${t.key}`}
                    onClick={() => setForm({ ...form, property_type: t.key })}
                    className={`text-left p-6 border transition-colors ${form.property_type === t.key ? "border-[#0F6E5B] bg-white" : "border-[#DEDCCF] bg-white hover:border-[#0D1F2D]"}`}
                  >
                    <t.icon size={28} strokeWidth={1.6} className={form.property_type === t.key ? "text-[#0F6E5B]" : "text-[#0D1F2D]"} />
                    <div className="font-display text-xl font-bold mt-4">{t.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-location">
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Pasul 2</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3">Unde este proprietatea?</h2>
              <div className="grid lg:grid-cols-2 gap-10 mt-10">
                <div>
                  <label className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Oraș</label>
                  <select
                    data-testid="input-city"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className={inputCls}
                  >
                    <option value="">— Alege orașul —</option>
                    {cities.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Cartier / zonă (opțional)</label>
                  <input
                    data-testid="input-neighborhood"
                    value={form.neighborhood}
                    onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                    placeholder="ex: Floreasca, Mănăștur, Centrul vechi"
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-characteristics">
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Pasul 3</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3">Caracteristici principale</h2>
              <div className="grid lg:grid-cols-3 gap-10 mt-10">
                <div>
                  <label className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Suprafață (m²) *</label>
                  <input
                    data-testid="input-surface" type="number" min="1"
                    value={form.surface}
                    onChange={(e) => setForm({ ...form, surface: e.target.value })}
                    placeholder="55" className={inputCls}
                  />
                </div>
                <div>
                  <label className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Număr camere</label>
                  <input
                    data-testid="input-rooms" type="number" min="0"
                    value={form.rooms}
                    onChange={(e) => setForm({ ...form, rooms: e.target.value })}
                    placeholder="2" className={inputCls}
                    disabled={form.property_type === "teren"}
                  />
                </div>
                <div>
                  <label className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">An construcție</label>
                  <input
                    data-testid="input-year" type="number" min="1800" max="2030"
                    value={form.year_built}
                    onChange={(e) => setForm({ ...form, year_built: e.target.value })}
                    placeholder="2012" className={inputCls}
                    disabled={form.property_type === "teren"}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-condition">
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Pasul 4</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3">Stare și dotări</h2>
              {form.property_type !== "teren" && (
                <>
                  <div className="mt-10 mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Stare</div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
                    {CONDITIONS.map((c) => (
                      <button
                        key={c.key}
                        data-testid={`condition-${c.key}`}
                        onClick={() => setForm({ ...form, condition: c.key })}
                        className={`text-left p-5 border transition-colors ${form.condition === c.key ? "border-[#0F6E5B] bg-white" : "border-[#DEDCCF] bg-white hover:border-[#0D1F2D]"}`}
                      >
                        <div className="font-display text-lg font-bold">{c.label}</div>
                        <div className="text-xs text-[#52616B] mt-1">{c.d}</div>
                      </button>
                    ))}
                  </div>
                  <div className="mt-10 mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Dotări (selectează toate cele aplicabile)</div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {FEATURES.map((f) => {
                      const on = form.features.includes(f.key);
                      return (
                        <button
                          key={f.key}
                          data-testid={`feature-${f.key}`}
                          onClick={() => toggleFeature(f.key)}
                          className={`px-4 h-10 text-sm border rounded-sm transition-colors ${on ? "bg-[#0D1F2D] text-[#F6F4EE] border-[#0D1F2D]" : "border-[#DEDCCF] hover:border-[#0D1F2D] bg-white"}`}
                        >
                          {on ? "✓ " : "+ "}{f.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              <div className="mt-10">
                <label className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">Detalii suplimentare (opțional)</label>
                <textarea
                  data-testid="input-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="ex: aproape de parc, orientare sudică, bloc nou..."
                  className={inputCls + " resize-none"}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-review">
              <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">§ Pasul 5</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3">Verifică și evaluează</h2>
              <div className="mt-10 border border-[#DEDCCF] bg-white divide-y divide-[#DEDCCF]">
                {[
                  ["Tip", PROPERTY_TYPES.find(t => t.key === form.property_type)?.label],
                  ["Oraș", cities.find(c => c.key === form.city)?.label || form.city],
                  ["Cartier", form.neighborhood || "—"],
                  ["Suprafață", `${form.surface} m²`],
                  ["Camere", form.rooms || "—"],
                  ["An", form.year_built || "—"],
                  ["Stare", CONDITIONS.find(c => c.key === form.condition)?.label],
                  ["Dotări", form.features.length ? form.features.map(f => FEATURES.find(x => x.key === f)?.label).join(", ") : "—"],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 px-6 py-4">
                    <div className="mono text-[11px] uppercase tracking-[0.22em] text-[#52616B]">{k}</div>
                    <div className="col-span-2 text-base">{v}</div>
                  </div>
                ))}
              </div>
              <button
                data-testid="submit-evaluate"
                onClick={submit}
                disabled={loading}
                className="mt-8 bg-[#0F6E5B] hover:bg-[#0A5447] disabled:opacity-50 transition-colors text-white px-8 h-14 rounded-sm font-medium inline-flex items-center gap-3"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Se analizează…</> : <>Evaluează proprietatea <ArrowRight size={18} /></>}
              </button>
            </div>
          )}
        </div>

        {/* Nav controls */}
        <div className="mt-14 flex items-center justify-between pt-6 border-t border-[#DEDCCF]">
          <button
            data-testid="btn-prev"
            onClick={prev}
            disabled={step === 0}
            className="flex items-center gap-2 text-sm text-[#52616B] hover:text-[#0D1F2D] disabled:opacity-30 transition-colors"
          >
            <ArrowLeft size={16} /> Înapoi
          </button>
          {step < steps.length - 1 && (
            <button
              data-testid="btn-next"
              onClick={next}
              className="flex items-center gap-2 bg-[#0D1F2D] hover:bg-[#0F6E5B] text-[#F6F4EE] px-6 h-11 rounded-sm font-medium transition-colors"
            >
              Continuă <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
